import { createContext, useContext } from 'react';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';

import { type UIConfig } from '../../../types';
import {
	type ChatRefreshV2Config,
	getChatRefreshV2DynamicConfig,
	initialState as initialV2DynamicConfig,
} from '../chat-refresh-v2-config';

type ChatRefreshV1Exp = {
	isRenderRefreshEnabled: boolean;
	isNewInputEnabled: boolean;
	isNewNavigationEnabled: boolean;
	isNewEmptyScreenEnabled: boolean;
};

type DynamicConfig = {
	rovoChatRefreshV2Config: ChatRefreshV2Config;
};

type ContextState = UIConfig & ChatRefreshV1Exp & { dynamicConfig: DynamicConfig };

const initialState: ContextState = {
	fullScreen: false,
	externalEmbed: false,
	customHandlers: {
		handleDeepLinkClick: undefined,
		handleSubmitSmartCreate: () => {},
	},
	isBitbucketBetaEnabled: false,
	stagingAreaRef: undefined,
	promptInputDroppableUrls: undefined,
	enableBrowserContextBanner: false,
	elements: {
		closeButton: true,
		newChatButton: true,
		fullscreenButton: true,
		createAgentButton: true,
		browseAgentsButton: true,
		agentsMenu: true,
		navbar: true,
		mainChatHeader: true,
		stagingAreaEnabled: false,
		voiceDictationButton: false,
	},
	// ChatRefreshV1Exp
	isRenderRefreshEnabled: false,
	isNewInputEnabled: false,
	isNewNavigationEnabled: false,
	isNewEmptyScreenEnabled: false,
	dynamicConfig: {
		rovoChatRefreshV2Config: initialV2DynamicConfig,
	},
};

export const uiConfigDefaults = initialState;

const uiConfig = createContext<ContextState>(initialState);
export const UIConfigProvider = uiConfig.Provider;
export const useUIConfig = () => useContext(uiConfig);

export const createUIConfig = (config?: Partial<UIConfig>): ContextState => {
	// Allow fullscreen override for branch deploy testing purposes
	const processedConfig: Partial<UIConfig> | undefined =
		localStorage.getItem('DEBUG_rovo_chat_fullscreen') === 'true' &&
		fg('rovo_chat_show_local_frontend_components')
			? {
					...config,
					fullScreen: true,
				}
			: config;
	const isRolloutInJiraEnabled =
		FeatureGates.getExperimentValue<string>('jira_ai_rovo_button', 'treatment', 'A') === 'B';

	const rovoChatRefreshV2Config = getChatRefreshV2DynamicConfig({ isRolloutInJiraEnabled });

	// Evaluate feature flag for browser context banner at conversation-assistant level
	const enableBrowserContextBanner =
		processedConfig?.enableBrowserContextBanner ??
		FeatureGates.getExperimentValue(
			'rovo_extension_dom_improvement',
			'rovo_extension_dom_context_improvement_enabled',
			false,
			{
				fireExperimentExposure: false,
			},
		);

	return {
		...{
			...initialState,
			...processedConfig,
			enableBrowserContextBanner,
			elements: {
				...initialState.elements,
				...processedConfig?.elements,
			},
			promptInputDroppableUrls: processedConfig?.promptInputDroppableUrls,
		},
		...{
			isRenderRefreshEnabled:
				isRolloutInJiraEnabled ||
				FeatureGates.getExperimentValue(
					'rovo_chat_refresh_v1_experiment',
					'isRenderRefreshEnabled',
					false,
				),
			isNewInputEnabled:
				isRolloutInJiraEnabled ||
				FeatureGates.getExperimentValue(
					'rovo_chat_refresh_v1_experiment',
					'isNewInputEnabled',
					false,
				),
			isNewNavigationEnabled:
				isRolloutInJiraEnabled ||
				FeatureGates.getExperimentValue(
					'rovo_chat_refresh_v1_experiment',
					'isNewNavigationEnabled',
					false,
				),
			isNewEmptyScreenEnabled:
				isRolloutInJiraEnabled ||
				FeatureGates.getExperimentValue(
					'rovo_chat_refresh_v1_experiment',
					'isNewEmptyScreenEnabled',
					false,
				),
		},
		dynamicConfig: {
			...initialState.dynamicConfig,
			rovoChatRefreshV2Config: {
				...rovoChatRefreshV2Config,
			},
		},
	};
};
